import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Card, CardContent, Typography } from "@mui/material";

type MetricKey = "nps" | "effortScore" | "satisfactionScore";

type Campaign = {
    campaignName: string;
    nps?: number;
    effortScore?: number;
    satisfactionScore?: number;
};

type ApiItem = {
    day: string;
    campaigns: Campaign[];
};

interface Props {
    response: ApiItem[];
    metric: MetricKey;
    label: string;
}

// дефол цвета
const palette = [
    "#4F46E5",
    "#EC4899",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
];

const GroupbyFilter: React.FC<Props> = ({ response, metric, label }) => {
    const { series, colors } = useMemo(() => {
        if (!Array.isArray(response)) {
            return { series: [], colors: [] };
        }

        const sorted = [...response].sort(
            (a, b) => new Date(a.day).getTime() - new Date(b.day).getTime()
        );

        const campaignNames = Array.from(
            new Set(
                sorted.flatMap((d) =>
                    d.campaigns.map((c) => c.campaignName)
                )
            )
        );

        const series = campaignNames.map((name) => ({
            name,
            data: sorted.map((dayItem) => {
                const found = dayItem.campaigns.find(
                    (c) => c.campaignName === name
                );

                return {
                    x: new Date(dayItem.day).getTime(),
                    y:
                        found?.[metric] !== undefined && found?.[metric] !== null
                            ? Number(found[metric]!.toFixed(1))
                            : null,
                };
            }),
        }));

        const colors = campaignNames.map(
            (_, index) => palette[index % palette.length]
        );

        return { series, colors };
    }, [response, metric]);

    const options: ApexOptions = {
        chart: {
            type: "line",
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        stroke: {
            width: 3,
            curve: "smooth",
        },
        markers: {
            size: 6,
            strokeWidth: 2,
            hover: { size: 8 },
        },
        colors,
        xaxis: {
            type: "datetime",
        },
        yaxis: {
            min: 0,
            max: 10,
        },
        grid: {
            strokeDashArray: 4,
        },
        legend: {
            position: "bottom",
        },
    };

    return (
        <Card sx={{ my: 2, minHeight: 485, background: "#ffffff", }}>
            <CardContent>
                <div style={{ marginBottom: 24 }}>
                    <Typography
                        sx={{ color: "primary.defaultText", fontSize: 24 }}
                    >
                        {label}
                    </Typography>
                </div>

                <Chart
                    options={options}
                    series={series}
                    type="line"
                    height={420}
                />
            </CardContent>
        </Card>
    );
};

export default GroupbyFilter;